import React, { useState, useEffect } from "react"
import useFetch from "hooks/use-new-fetch"

export const UserContext = React.createContext({})

export function UserProvider({ children }) {
  // Currently, the user information is provided directly on the window object.
  // This utility is written using Context and hooks to future proof it for changes
  // where the user is provided in other ways or can change without the page
  // refreshing.
  const initialUser = (window.app && window.app.user) || {}
  const [user, setUser] = useState(initialUser)
  const [refreshAllowedActivitiesRequest] = useFetch("/user/allowed_activities")

  const refreshAllowedActivities = async () => {
    const { data } = await refreshAllowedActivitiesRequest.get()
    setUser(prevUser => ({ ...prevUser, allowed_activities: data }))
  }

  useEffect(() => {
    window.user = user
  }, [user])

  return (
    <UserContext.Provider value={{ ...user, refreshAllowedActivities }}>
      {children}
    </UserContext.Provider>
  )
}

export default function useCurrentUser() {
  const user = React.useContext(UserContext)

  if (!user) {
    console.warn(
      "Information about the current user is not set on `window.app`, or the UserProvider component is not used in this tree."
    )
  }

  const allowedActivities = user?.allowed_activities || []

  const canAddSequence = allowedActivities.includes("sequences.create")
  const canAddSubscribersWithoutCreatorNetwork = allowedActivities.includes(
    "subscribers.add_without_creator_network"
  )
  const canAddVA = allowedActivities.includes("va.create")
  const canCancelAccount = allowedActivities.includes("account.cancel")
  const canComplianceModify = allowedActivities.includes("compliance_modify")
  const canDisableRecommendations = allowedActivities.includes(
    "disable_recommendations"
  )
  const canUsePaidRecommendations = allowedActivities.includes(
    "paid_recommendations"
  )
  const canUseNativePaidRecommendations = allowedActivities.includes(
    "native_paid_recommendations"
  )
  const canRemoveCkBranding = allowedActivities.includes("remove_ck_branding")
  const canUseAPI = allowedActivities.includes("api")
  const canViewTemplateLibrary = allowedActivities.includes(
    "va.template_library"
  )
  const canViewAds = allowedActivities.includes("programmatic_ads")
  const canActivateSequence = allowedActivities.includes("sequences.activate")
  const accountCanAccessApps = allowedActivities.includes("apps")
  const canInstallApps = allowedActivities.includes("apps.install")
  const canUsePolls = allowedActivities.includes("polls")

  return {
    ...user,
    canActivateSequence,
    canAddSequence,
    canAddSubscribersWithoutCreatorNetwork,
    canAddVA,
    canCancelAccount,
    canComplianceModify,
    canDisableRecommendations,
    canRemoveCkBranding,
    canUseAPI,
    canUseNativePaidRecommendations,
    canUsePaidRecommendations,
    canUsePolls,
    canViewAds,
    accountCanAccessApps,
    canInstallApps,
    canViewTemplateLibrary
  }
}
